import { License_RegisterAgentAction, License_RegistrationType, useAgentsLicenseConfigurationLazyQuery } from '@/graphql/codegen/generated/graphql';
import { IKCoordinatorsService } from '@/modules/CoordinatorsService/CoordinatorService.InjectionKeys';
import { computed, inject, ref } from 'vue';
import { SelectedAgent } from '../AgentActionMenu.types';
import { CIInitiatorLicenseTypeMap, helperLicenseTypeMap, helperTypeslist, initiatorLicenseTypeMap, initiatorTypeslist, NULL_LICENSE_OPTION } from './AgentActionLicenseConf.const';

/* It checks if the selection is a CI initiator license. */
export const isCIInitiatorLicenseSelected = (selection: typeof initiatorTypeslist[number]) => selection?.label === 'Floating CI initiator' || selection?.label === 'Fixed CI initiator';

/* It checks if the selection is a CI initiator license. */
export const isInitiatorLicenseSelected = (selection: typeof initiatorTypeslist[number]) => selection?.label === 'Fixed initiator' || selection?.label === 'Floating initiator';

/* It checks if the selection is a fixed license. */
export const isFixedLicenseSelected = (selection: typeof initiatorTypeslist[number] | typeof helperTypeslist[number]) =>
  selection?.label === 'Fixed initiator' || selection?.label === 'Fixed helper' || selection?.label === 'Fixed CI initiator';

export const calcMaxCoresForAgentByPrecentage = (agent: SelectedAgent, percentage: number) => {
  /**
   * It calculates the maximum number of cores that an agent can use based on the percentage of the total
   * number of cores that the agent has
   * @param {Agent} agent - The agent object
   * @param {number} percentage - The percentage of the agent's CPU that you want to use.
   */
  const cpuLogical = agent.performance?.cpuLogical || 1;
  return Math.max(Math.ceil((cpuLogical * percentage) / 100), 1);
};

const getSelectedLicensesOptionsForSingleAgent = (agent: SelectedAgent) => {
  const ciInitiatorSelection = agent.license?.ciInitiatorType !== License_RegistrationType.None ? CIInitiatorLicenseTypeMap[agent.license?.ciInitiatorType || License_RegistrationType.None] : null;
  const initiatorSelection = agent.license?.initiatorType !== License_RegistrationType.None ? initiatorLicenseTypeMap[agent.license?.initiatorType || License_RegistrationType.None] : null;
  const helperSelection = helperLicenseTypeMap[agent.license?.helperType || License_RegistrationType.None];

  return {
    initiator: ciInitiatorSelection || initiatorSelection || initiatorLicenseTypeMap[License_RegistrationType.None],
    helper: helperSelection || helperLicenseTypeMap[License_RegistrationType.None],
  };
};

export const useAgentsLicenseConfigurationData = (agents: SelectedAgent[]) => {
  const coordinatorsService = inject(IKCoordinatorsService);
  const { result: licenseConfigurationQuery, onResult, load } = useAgentsLicenseConfigurationLazyQuery(() => ({ coordinatorId: coordinatorsService?.selectedCoordinator.value || '' }), {});
  const singleAgent = computed(() => (agents.length === 1 ? { assignedHelperCores: agents[0].license?.helperCores || 0 } : false));
  const CURRENT_SETTINGS_HELPER_CORES_ASSIGNMENT_PRECENTAGE = computed(() => licenseConfigurationQuery.value?.ibManagerEntities?.settings?.windows?.license?.helperCoresAssignmentPercentage || 100);

  const getOriginalState = () =>
    ({
      allowCoresChange: singleAgent.value && agents[0]?.license?.helperType !== License_RegistrationType.None,
      selectedCores: singleAgent.value ? singleAgent.value.assignedHelperCores : CURRENT_SETTINGS_HELPER_CORES_ASSIGNMENT_PRECENTAGE.value,
      licenseSelections: singleAgent.value
        ? getSelectedLicensesOptionsForSingleAgent(agents[0])
        : {
            initiator: NULL_LICENSE_OPTION,
            helper: NULL_LICENSE_OPTION,
          },
    } as const);

  /* A hook that returns the default state for the license configuration modal. */
  const useInitiateState = () => {
    const allowCoresChange = ref<boolean>(false);
    const selectedCores = ref<number>(0);
    const licenseSelections = ref<{ initiator: typeof initiatorTypeslist[number]; helper: typeof helperTypeslist[number] }>({
      initiator: NULL_LICENSE_OPTION,
      helper: NULL_LICENSE_OPTION,
    });
    onResult(() => {
      const initateState = getOriginalState();
      allowCoresChange.value = initateState.allowCoresChange;
      selectedCores.value = initateState.selectedCores;
      licenseSelections.value = initateState.licenseSelections;
    });
    load();

    const isDirty = computed(() => {
      const initateState = getOriginalState();
      const state = {
        selectedCores: selectedCores.value !== initateState.selectedCores,
        licenseSelections: {
          helper: licenseSelections.value?.helper.label !== initateState.licenseSelections.helper.label,
          initiator: licenseSelections.value?.initiator.label !== initateState.licenseSelections.initiator.label,
        },
        allowCoresChange: allowCoresChange.value !== initateState.allowCoresChange,
      };
      return {
        status: state.selectedCores || state.licenseSelections.helper || state.licenseSelections.initiator || state.allowCoresChange,
        state,
      };
    });

    const licenseAgentRequest = (agentsList: SelectedAgent[]) => {
      /**
       * A function that is called when the submit button is clicked. It calls the setAgentsLicense function
       * from the useAgentActionsMenu hook.
       */
      const getRequestedCores = (agent: SelectedAgent) => {
        // Checking if the agent is single, if it is it will return 0 if the allowCoresChange is false,
        // otherwise it will return the selectedCores value.
        if (singleAgent.value) {
          if (!allowCoresChange.value) return 0;
          return selectedCores?.value;
        }

        /**
         * In case of bulk Checking if the allowCoresChange is true, if it is it will return the calcMaxCoresForAgentByPrecentage function with
         * the agent and the selectedCores value.
         * If the agent is helper it will return the helperCores value of the agent license, otherwise it will return the
         * calcMaxCoresForAgentByPrecentage function with the agent and the selectedCores value.
         */
        if (allowCoresChange.value) return calcMaxCoresForAgentByPrecentage(agent, selectedCores?.value);
        if (agent.metadata?.isHelper) return agent.license?.helperCores || 0;
        return calcMaxCoresForAgentByPrecentage(agent, selectedCores?.value);
      };
      return agentsList.map(agent => {
        const agentId = agent?.id as string;
        const helperLicense = (licenseSelections.value.helper?.value as License_RegistrationType) || agent.license?.helperType || License_RegistrationType.None;
        const requestedCores = getRequestedCores(agent);
        /**
         * Important Note: Only one initiator license(CI initiator/regular initiator) can be assigned to agent,
         * therfore it will assigned the selected initiator only the related initiator license type, the other one will be assigned to be none at that case.
         * And if initiator license is not dirty at all, it will send the current state of the initiator license of the agent.
         */
        const ciInitiatorLicense = (
          licenseSelections.value.initiator?.value && isCIInitiatorLicenseSelected(licenseSelections.value.initiator) ? licenseSelections.value.initiator?.value : License_RegistrationType.None
        ) as License_RegistrationType;
        const initiatorLicense = (
          licenseSelections.value.initiator?.value && isInitiatorLicenseSelected(licenseSelections.value.initiator) ? licenseSelections.value.initiator?.value : License_RegistrationType.None
        ) as License_RegistrationType;

        return {
          action: License_RegisterAgentAction.LicenseConfig,
          agentId,
          helperLicense,
          requestedCores,
          coordinatorId: coordinatorsService?.selectedCoordinator.value || '',
          ciInitiatorLicense: isDirty.value.state.licenseSelections.initiator ? ciInitiatorLicense : agent.license?.ciInitiatorType || License_RegistrationType.None,
          initiatorLicense: isDirty.value.state.licenseSelections.initiator ? initiatorLicense : agent.license?.initiatorType || License_RegistrationType.None,
        };
      });
    };

    return { isDirty, licenseSelections, selectedCores, allowCoresChange, singleAgent, licenseAgentRequest };
  };

  /* A hook that returns the default constant data for the license configuration modal. */
  const useInitiateConstantData = () => {
    const HELPER_LICENSE_OPTIONS = computed(() => {
      /* A computed ref that returns an array of helper license options that are available. */
      const { totalFixedCores, totalFloatingCores } = licenseConfigurationQuery.value?.ibManagerEntities?.license?.windows?.summary || {};
      return helperTypeslist.filter(option => {
        if (option.label === NULL_LICENSE_OPTION.label && singleAgent.value) return false;
        if (option.label === 'Fixed helper' && !totalFixedCores) return false;
        if (option.label === 'Floating helper' && !totalFloatingCores) return false;
        return true;
      });
    });

    /* A computed ref that returns an array of initiator license options that are available. */
    const INITIATOR_LICENSE_OPTIONS = computed(() => {
      const { totalFixedInitiators, totalFixedCIInitiators, totalFloatingCIInitiators, totalFloatingInitiators } = licenseConfigurationQuery.value?.ibManagerEntities?.license?.windows?.summary || {};
      return initiatorTypeslist.filter(option => {
        if (option.label === NULL_LICENSE_OPTION.label) return false;
        if (option.label === 'Fixed initiator' && !totalFixedInitiators) return false;
        if (option.label === 'Fixed CI initiator' && !totalFixedCIInitiators) return false;
        if (option.label === 'Floating initiator' && !totalFloatingInitiators) return false;
        if (option.label === 'Floating CI initiator' && !totalFloatingCIInitiators) return false;
        return true;
      });
    });

    return {
      CURRENT_SETTINGS_HELPER_CORES_ASSIGNMENT_PRECENTAGE,
      HELPER_LICENSE_OPTIONS,
      INITIATOR_LICENSE_OPTIONS,
    };
  };

  return { useInitiateState, useInitiateConstantData, getOriginalState };
};
